import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE, getAuthHeader } from "../api/api";

const CaseManagement = () => {
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [newCase, setNewCase] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    is_pending: false,
  });
  const [assign, setAssign] = useState({ case_id: "", username: "" });
  const [editingId, setEditingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [editForm, setEditForm] = useState({
    title: "",
    updates: [],
    new_description: "",
    is_pending: false,
  });

  const fetchCases = async () => {
    const res = await axios.get(`${API_BASE}/user/my-cases`, getAuthHeader());
    setCases(res.data.cases);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/users`, getAuthHeader());
      if (res.data.status === "success") {
        setUsers(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchCases();
    fetchUsers();
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setEditForm({
      title: c.title,
      updates: JSON.parse(JSON.stringify(c.updates || [])),
      new_description: "",
      is_pending: c.is_pending || false,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const payload = {
      ...newCase,
      scheduled_at: newCase.scheduled_at
        ? new Date(newCase.scheduled_at).toISOString()
        : null,
    };
    await axios.post(`${API_BASE}/admin/cases`, payload, getAuthHeader());
    setNewCase({
      title: "",
      description: "",
      scheduled_at: "",
      is_pending: false,
    });
    fetchCases();
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    await axios.put(`${API_BASE}/admin/assign`, assign, getAuthHeader());
    fetchCases();
    setAssign({ case_id: "", username: "" });
  };

  const handleDeleteCase = async (id) => {
    if (!window.confirm("Confirm case deletion?")) return;
    try {
      await axios.delete(`${API_BASE}/admin/cases/${id}`, getAuthHeader());
      fetchCases();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handleAdminUpdate = async (id) => {
    try {
      await axios.put(
        `${API_BASE}/admin/update-case`,
        {
          case_id: id,
          title: editForm.title,
          updates: editForm.updates,
          new_description: editForm.new_description,
          is_pending: editForm.is_pending,
        },
        getAuthHeader(),
      );
      setEditingId(null);
      fetchCases();
    } catch (err) {
      alert("Update failed");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-2rem)] overflow-hidden relative">
      <div className="w-full lg:w-1/3 space-y-6 overflow-y-auto lg:overflow-visible">
        <section className="bg-white p-6 shadow rounded border-t-4 border-blue-600 lg:sticky lg:top-0">
          <h2 className="font-bold mb-4">Create Case</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <input
              className="w-full border p-2 text-sm"
              placeholder="Title"
              value={newCase.title}
              onChange={(e) =>
                setNewCase({ ...newCase, title: e.target.value })
              }
            />
            <textarea
              className="w-full border p-2 text-sm"
              placeholder="Initial Description"
              value={newCase.description}
              onChange={(e) =>
                setNewCase({ ...newCase, description: e.target.value })
              }
            />
            <input
              type="datetime-local"
              className="w-full border p-2 text-sm"
              value={newCase.scheduled_at}
              onChange={(e) =>
                setNewCase({ ...newCase, scheduled_at: e.target.value })
              }
            />
            <div className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                id="new-pending"
                checked={newCase.is_pending}
                onChange={(e) =>
                  setNewCase({ ...newCase, is_pending: e.target.checked })
                }
              />
              <label
                htmlFor="new-pending"
                className="text-xs font-bold text-red-600 uppercase"
              >
                Mark as Pending
              </label>
            </div>
            <button className="w-full bg-blue-600 text-white py-2 rounded font-bold">
              Add Case
            </button>
          </form>
        </section>

        <section className="bg-white p-6 shadow rounded border-t-4 border-green-600 lg:sticky lg:top-[380px]">
          <h2 className="font-bold mb-4">Assign Case</h2>
          <form onSubmit={handleAssign} className="space-y-3">
            <select
              className="w-full border p-2 text-sm"
              value={assign.case_id}
              onChange={(e) =>
                setAssign({ ...assign, case_id: e.target.value })
              }
              required
            >
              <option value="">Select a Case</option>
              {cases.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.title}
                </option>
              ))}
            </select>
            <select
              className="w-full border p-2 text-sm"
              value={assign.username}
              onChange={(e) =>
                setAssign({ ...assign, username: e.target.value })
              }
              required
            >
              <option value="">Select a User</option>
              {users.map((u) => (
                <option key={u.username} value={u.username}>
                  {u.username}
                </option>
              ))}
            </select>
            <button className="w-full bg-green-600 text-white py-2 rounded font-bold">
              Confirm Assignment
            </button>
          </form>
        </section>
      </div>

      <div className="w-full lg:w-2/3 bg-white p-6 shadow rounded overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Case Overview</h2>
        <div className="space-y-4">
          {cases.map((c) => {
            const isExpanded = expandedIds.includes(c._id);
            const isBeingEdited = editingId === c._id;
            return (
              <div
                key={c._id}
                className={`p-4 border rounded hover:bg-gray-50 ${c.is_pending ? "border-red-500 bg-red-50/30" : ""}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 mr-4">
                    {isBeingEdited ? (
                      <div className="space-y-2">
                        <input
                          className="w-full border p-1 font-bold text-blue-700"
                          value={editForm.title}
                          onChange={(e) =>
                            setEditForm({ ...editForm, title: e.target.value })
                          }
                        />
                        <div className="flex items-center gap-2 p-2 bg-red-100 rounded">
                          <input
                            type="checkbox"
                            id={`edit-pending-${c._id}`}
                            checked={editForm.is_pending}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                is_pending: e.target.checked,
                              })
                            }
                          />
                          <label
                            htmlFor={`edit-pending-${c._id}`}
                            className="text-xs font-bold text-red-700 uppercase"
                          >
                            Is Pending (Alert Active)
                          </label>
                        </div>
                        {/* Edit Previous Entries */}
                        <div className="mt-3">
                          <label className="text-[10px] font-bold text-gray-500 uppercase">
                            Edit Entries
                          </label>
                          <div className="space-y-2 mt-1">
                            {editForm.updates.map((u, idx) => (
                              <textarea
                                key={idx}
                                className="w-full border p-2 text-sm text-gray-600 bg-white"
                                value={u.text}
                                onChange={(e) => {
                                  const newUpdates = [...editForm.updates];
                                  newUpdates[idx].text = e.target.value;
                                  setEditForm({
                                    ...editForm,
                                    updates: newUpdates,
                                  });
                                }}
                                rows="2"
                              />
                            ))}
                          </div>
                        </div>
                        <div className="mt-2 border-t pt-2">
                          <label className="text-[10px] font-bold text-gray-500 uppercase">
                            Add New Entry
                          </label>
                          <textarea
                            className="w-full border p-2 text-sm mt-1"
                            placeholder="New description..."
                            value={editForm.new_description}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                new_description: e.target.value,
                              })
                            }
                            rows="2"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="font-bold text-blue-700 flex items-center gap-2">
                          {c.title}
                          {c.is_pending && (
                            <span className="bg-red-600 text-white text-[9px] px-2 py-0.5 rounded-full animate-pulse">
                              PENDING
                            </span>
                          )}
                        </h3>
                        <div className="text-[11px] text-gray-500 mt-1">
                          <span className="font-bold uppercase">
                            Scheduled:
                          </span>{" "}
                          {c.scheduled_at
                            ? new Date(c.scheduled_at + "Z").toLocaleString(
                                "en-IN",
                              )
                            : "Immediate"}
                        </div>
                        <button
                          onClick={() => toggleExpand(c._id)}
                          className="mt-2 text-[10px] font-bold text-blue-600 uppercase hover:underline"
                        >
                          {isExpanded ? "Hide Details" : "Show Details"}
                        </button>
                        {isExpanded && (
                          <div className="mt-3 space-y-2">
                            {c.updates?.map((update, idx) => (
                              <div
                                key={idx}
                                className="p-2 bg-white border-l-4 border-blue-500 text-sm shadow-sm"
                              >
                                <span className="block text-[10px] font-mono text-gray-400 mb-1">
                                  {new Date(update.timestamp).toLocaleString()}
                                </span>
                                <span className="text-gray-700 whitespace-pre-wrap">
                                  {update.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-[10px] font-mono text-gray-400">
                      ID: {c._id}
                    </span>
                    {isBeingEdited ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAdminUpdate(c._id)}
                          className="text-xs bg-green-600 text-white px-3 py-1 rounded font-bold"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-xs bg-gray-400 text-white px-3 py-1 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(c)}
                          className="text-xs bg-gray-100 hover:bg-blue-600 hover:text-white px-2 py-1 rounded border transition-colors font-semibold"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCase(c._id)}
                          className="text-xs bg-red-100 text-red-600 hover:bg-red-600 hover:text-white px-2 py-1 rounded border border-red-200 transition-colors font-semibold"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CaseManagement;
